import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import SelectDropdown from 'react-select';
import Col from '../Col';
import Input from '../Input';
import Checkbox from '../Checkbox';
import { selectAssetFormData } from 'src/store/selectors/assetForm';
import { selectContacts } from 'src/store/selectors/contacts';
import { selectValidation } from 'src/store/selectors/Validation';
import { selectAssets } from 'src/store/selectors/assets';
import {
  selectFormOptions,
  selectPropertiesCatalog,
} from 'src/store/selectors/formOptions';
import {
  setAssetFormData as setAssetFormDataAction,
  clearAssetFormData as clearAssetFormDataAction,
} from '../../store/actions/assetForm';
import {
  clearValidation as clearValidationAction,
  fetchPropertyCatalog,
  clearPropertiesCatalog,
} from '../../store/actions/assets';
import { variables } from 'src/Tenants';
import { selectStyles } from 'src/utils/SharedStyles/SharedTableStyles';
import ModernDatePicker from 'src/components/SharedComponents/ModernDatePicker/ModernDatePicker';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import AssetFormSkeleton from './skeletons/AssetFormSkeleton';
import Loader from '../Loader';

const AssetForm = (props) => {
  const { propertyId, onClose, newAsset, selectedAreaId, buttonPressed } =
    props;

  const dispatch = useDispatch();

  // Todo: Refactor this out of Redux + its deps.
  const assetFormData = useSelector(selectAssetFormData);
  const assets = useSelector(selectAssets);
  const contacts = useSelector(selectContacts);
  const validation = useSelector(selectValidation);
  const formOptions = useSelector(selectFormOptions);
  const catalog = useSelector(selectPropertiesCatalog);
  const loader = useSelector(selectFormLoader);

  const [hasObservedLoading, setHasObservedLoading] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  useEffect(() => {
    if (loader && !hasObservedLoading) {
      setHasObservedLoading(true);
    }
    if (hasObservedLoading && !loader && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [loader, hasObservedLoading, hasInitiallyLoaded]);

  const [state, setState] = useState({
    display: false,
    vendorSearch: '',
    displayItem: false,
    itemSearch: '',
    isChanged: false,
    room: assetFormData.areaId ? assetFormData.areaId : '',
  });

  const setAssetFormData = (data: any) => dispatch(setAssetFormDataAction(data));
  const clearAssetFormData = () => dispatch(clearAssetFormDataAction());
  const clearValidation = () => dispatch(clearValidationAction());
  const onFetchPropertyCatalog = (id: any) => dispatch(fetchPropertyCatalog(id));
  const onClearPropertiesCatalog = () => dispatch(clearPropertiesCatalog());

  useEffect(() => {
    const init = async () => {
      if (!(assetFormData && assetFormData.dateOfPurchase)) {
        setAssetFormData({
          dateOfPurchase: moment(new Date()).format('MM-DD-YYYY'),
        });
      }
      await onClearPropertiesCatalog();
      onFetchPropertyCatalog(propertyId);
      clearValidation();
    };
    init();
  }, []);

  useEffect(() => {
    let assetWarrantyDefaultStartDate =
      assetFormData.assetWarrantyDefaultStartDate;

    if (selectedAreaId) {
      if (!assetFormData.areaId) {
        let selectedData = assets.filter(
          (room: any) => room.areaId === selectedAreaId
        )[0];

        setAssetFormData({
          roomType:
            selectedData && selectedData.customLabel
              ? selectedData.customLabel
              : selectedData.name,
          areaId: selectedData.areaId,
        });
      }
    }

    if (!(assetFormData && assetFormData.dateOfPurchase)) {
      setAssetFormData({
        dateOfPurchase: moment(new Date()).format('MM-DD-YYYY'),
      });
    }

    if (validation.status === true) {
      if (
        validation.message ===
        'This serial number already exists, please try another.'
      ) {
        clearValidation();
      } else if (
        validation.message === 'Success' &&
        buttonPressed === 'SaveAndClose'
      ) {
        onClose();
        clearAssetFormData();
        clearValidation();
      } else if (
        validation.message === 'Success' &&
        buttonPressed === 'SaveAndAdd'
      ) {
        clearAssetFormData();
        setAssetFormData({
          assetWarrantyDefaultStartDate: assetWarrantyDefaultStartDate,
          dateOfPurchase: assetWarrantyDefaultStartDate,
        });
        clearValidation();
      }
    }
  }, [assets, validation, buttonPressed]);

  const onHandleItemChange = (object: any) => {
    if (!object) return;

    let data: any = {};
    let propertiesCatalog = formOptions.propertiesCatalog;

    for (var i = 0; i < propertiesCatalog.length; i++) {
      if (propertiesCatalog[i].id === object.value) {
        data.item = {
          name: propertiesCatalog[i].name,
          make: propertiesCatalog[i].makeName,
          model: propertiesCatalog[i].model,
        };
      }
    }
    data.itemId = object.value;
    data.itemName = object.label;

    setAssetFormData({
      ...assetFormData,
      ...data,
    });

    setState((prev: any) => ({
      ...prev,
      displayItem: false,
      itemSearch: '',
      isChanged: false,
    }));
  };

  const currentItemValue: any = useMemo(() => {
    if (!assetFormData.itemId) return null;

    return (
      catalog.find((opt: any) => opt.value === assetFormData.itemId) || {
        value: assetFormData.itemId,
        label: assetFormData.itemName,
      }
    );
  }, [assetFormData.itemId, assetFormData.itemName, catalog]);

  const roomOptions: any = useMemo(
    () =>
      assets.map((assetRoom: any) => ({
        value: assetRoom.areaId,
        label: assetRoom.customLabel || assetRoom.name,
      })),
    [assets]
  );

  const currentRoomValue = useMemo(() => {
    return (
      roomOptions.find((opt: any) => opt.value === assetFormData.areaId) || null
    );
  }, [assetFormData.areaId, roomOptions]);

  const onHandleRoomChange = (option: any) => {
    if (!option) {
      setAssetFormData({ roomType: '', areaId: '' });
      return;
    }
    const selectedData: any = assets.find(
      (room: any) => room.areaId === option.value
    );
    setAssetFormData({
      roomType: selectedData.customLabel || selectedData.name,
      areaId: selectedData.areaId,
    });
  };

  let vendorContacts = contacts.filter((item: any) =>
    item.roles && item.roles.length > 0
      ? item.roles[0].role.name === 'Vendor'
      : []
  );

  let condition;

  if (
    assetFormData &&
    assetFormData.assetWarrantyDefaultStartDate &&
    assetFormData &&
    assetFormData.dateOfPurchase
  ) {
    if (
      moment(assetFormData.assetWarrantyDefaultStartDate).format(
        'MM-DD-YYYY'
      ) === moment(assetFormData.dateOfPurchase).format('MM-DD-YYYY')
    ) {
      condition = true;
    } else {
      condition = false;
    }
  }

  if (!hasInitiallyLoaded) {
    return <AssetFormSkeleton />;
  }

  if (loader) {
    return <Loader containerHeight="40rem" />;
  }

  return (
    <>
      <div className="input_group" style={{ marginBottom: '20px' }}>
        <label
          style={{
            color: 'black',
            marginBottom: '8px',
            display: 'flex',
            fontWeight: 600,
          }}
        >
          Item
        </label>
        <SelectDropdown
          value={currentItemValue}
          onChange={onHandleItemChange}
          options={catalog}
          styles={selectStyles}
          placeholder="Search and select an item..."
          isClearable={false}
        />
      </div>
      <div className="input_group" style={{ marginBottom: '20px' }}>
        <label
          style={{
            color: 'black',
            marginBottom: '8px',
            display: 'flex',
            fontWeight: 600,
          }}
        >
          {variables.roomName}
        </label>
        <SelectDropdown
          value={currentRoomValue}
          onChange={onHandleRoomChange}
          options={roomOptions}
          styles={selectStyles}
          placeholder={'(Select ' + variables.roomName + ')'}
          isDisabled={assetFormData.allRooms === true}
          isClearable={false}
        />
      </div>
      <div style={{ marginBottom: '2.5rem' }}>
        {assetFormData.id ? null : (
          <Checkbox
            label={'All ' + variables.roomName + 's'}
            value={assetFormData.allRooms}
            onChange={() =>
              setAssetFormData({
                allRooms: !(
                  assetFormData.allRooms && assetFormData.allRooms === true
                ),
              })
            }
          />
        )}
        {condition ? (
          <div
            style={{
              alignItems: 'flex-start',
              display: 'flex',
              marginTop: '8px',
              color: '#D3D3D3',
            }}
          >
            {`Default date is set at the ${variables.suite} level`}.
          </div>
        ) : null}
      </div>
      <Input
        value={
          assetFormData.vendorName
            ? assetFormData.vendorName
            : state.vendorSearch
        }
        onChange={(e: any) => {
          setAssetFormData({
            vendorName: e?.target?.value,
            vendorId: null,
          });
          setState((prev: any) => ({
            ...prev,
            display: true,
            vendorSearch: e?.target?.value,
          }));
        }}
        label="Vendor"
        placeholder="Start typing the Vendor's name to choose one"
        block
      />
      <Col>
        {state.vendorSearch
          ? vendorContacts
              .filter(
                ({ firstname, lastname }: any) =>
                  firstname
                    .toLowerCase()
                    .indexOf(state.vendorSearch.toLowerCase()) > -1 ||
                  lastname
                    .toLowerCase()
                    .indexOf(state.vendorSearch.toLowerCase()) > -1
              )
              .map((value: any, i: any) => {
                return (
                  <div
                    onClick={() => {
                      setAssetFormData({
                        vendorId: value.id,
                        vendorName: value.firstname + ' ' + value.lastname,
                      });
                      setState((prev: any) => ({
                        ...prev,
                        display: false,
                        vendorSearch: '',
                      }));
                    }}
                    className="input__autoComplete"
                    key={i}
                  >
                    <span>{value.firstname + ' ' + value.lastname}</span>
                  </div>
                );
              })
          : null}
      </Col>
    </>
  );
};

export default AssetForm;
